import React, { useEffect, useMemo, useRef } from 'react';
import { LoaderCircle } from 'lucide-react';
import { ClipMessage, VersionCard } from '../ds';
import { AnalysisCard } from './AnalysisCard';
import { AnalyzingCard } from './AnalyzingCard';
import { ChatText } from './ChatText';
import { buildThread, versionCardProps } from '../../utils/review';

// Shown while a reply is on its way (an analysis gets the AnalyzingCard)
const Working = ({ children }) => (
  <div role="status" className="flex items-center gap-space-3 text-small text-muted font-text">
    <LoaderCircle size={18} strokeWidth={2.5} aria-hidden="true" className="animate-spin motion-reduce:animate-none text-amber" />
    {children}
  </div>
);

/**
 * A clip's chat: older versions collapsed, the latest version's full analysis, and the
 * conversation around them, oldest first. Keeps the newest turn in view.
 */
export const Thread = ({ versions, messages, pending, pendingMessage, onShowMetering }) => {
  const items = useMemo(() => buildThread(versions, messages), [versions, messages]);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [items.length, pending, pendingMessage]);

  return (
    <div className="flex flex-col gap-space-5">
      {items.length === 0 && !pending && (
        <p className="m-0 text-small text-muted">No versions yet. Upload one below to get a score.</p>
      )}
      {items.map((item) => {
        if (item.type === 'message') {
          const { message } = item;
          return message.role === 'user'
            ? <ClipMessage key={item.key} note={message.content} />
            : <ChatText key={item.key} text={message.content} />;
        }
        const { version } = item;
        if (!item.latest) return <VersionCard key={item.key} {...versionCardProps(version)} />;
        return (
          <React.Fragment key={item.key}>
            <ClipMessage filename={version.filename} version={version.version} note={version.note} />
            <AnalysisCard version={version} onShowMetering={() => onShowMetering(version)} />
          </React.Fragment>
        );
      })}
      {pending && (
        <>
          <ClipMessage filename={pending.filename} version={pending.version} note={pending.note} />
          <AnalyzingCard info={pending} />
        </>
      )}
      {pendingMessage && (
        <>
          <ClipMessage note={pendingMessage} />
          <Working>Thinking…</Working>
        </>
      )}
      <div ref={endRef} />
    </div>
  );
};
